/**
 * The agent browser can hold one WebRTC call. A second auto-dial transfer
 * used to hang up the first whenever Telnyx had not yet flipped it to
 * `active` — that dropped a live merchant the instant the next invite landed.
 *
 * Keep whoever is already occupying the client. The new transfer is rejected
 * so live-connect retry can try another agent instead of stealing this one.
 *
 * Occupancy is read from the call itself, never from the UI's status string.
 * That is a reversal, and it is deliberate.
 *
 * The status used to be an independent reason to reject: `occupied` meant
 * `status === 'in-call'`, and it rejected on its own even with no call to point
 * at. The two can disagree, and when they do the status is the one that is
 * wrong — every path that puts a call up sets `activeCallRef` and the status
 * together, so "in-call with no call" is a contradiction, not a call we failed
 * to record. Resolving it in favour of the status turned one stuck flag into a
 * browser that refused every transfer for the rest of the shift, silently: the
 * heartbeat reports the React state, which still read `ready`, so the dialer
 * kept routing merchants to an agent whose client hung up on all of them. Each
 * refusal spends one of the merchant's three agent attempts, so three of them
 * end the call as BRIDGE_TIMEOUT.
 *
 * The two failures are not symmetric. Accepting a transfer we should have
 * refused costs one merchant, once, and live-connect retry re-routes them.
 * Refusing one we should have accepted costs every merchant sent to that agent
 * until the tab is reloaded. So the call object decides, and `isCallStillUp`
 * keeps the safe direction inside that: an unknown or missing state still reads
 * as a live call, and only a state the SDK has positively marked as ended
 * releases the browser.
 */
export function shouldRejectIncomingCall(args: {
  existingCall?: { id?: string | null; state?: string | null } | null;
  incomingCallId: string;
  occupyingCallIds?: Array<string | null | undefined>;
}): boolean {
  if (!args.incomingCallId) return false;

  for (const id of args.occupyingCallIds || []) {
    if (id && id !== args.incomingCallId) return true;
  }

  // An update about the call we are already holding is not a second invite.
  if (args.existingCall?.id && args.existingCall.id === args.incomingCallId) return false;

  return isCallStillUp(args.existingCall);
}

/** Telnyx call states that mean the leg is gone. Everything else is still up. */
const ENDED_CALL_STATES = new Set(['hangup', 'destroy', 'purge']);

/**
 * Is this call object still a real call, or the wreckage of one?
 *
 * `activeCallRef` is not cleared on every path a call can end, so a reference
 * to a dead call outlives it. An unknown or missing state reads as still up:
 * the expensive mistake here is deciding a live call is over and letting the
 * next transfer take the browser out from under the agent.
 */
export function isCallStillUp(call: { state?: string | null } | null | undefined): boolean {
  if (!call) return false;
  const state = String(call.state ?? '').trim().toLowerCase();
  if (!state) return true;
  return !ENDED_CALL_STATES.has(state);
}

/**
 * May the browser place a park leg right now?
 *
 * Never beside a live call. A park call placed mid-conversation took the live
 * call's place in this client: the agent heard silence, wrap-up opened over a
 * call still in progress, and their disposition then hung up the merchant
 * (2026-09-10, JC & CO). The server asked to park on every heartbeat because
 * the agent had no park session, and nothing here checked for a call.
 *
 * Status and call object are both read, and either one saying "busy" is
 * enough — the opposite resolution to `shouldRejectIncomingCall`, for the same
 * reason in reverse. There, trusting a stuck status refused every merchant.
 * Here, a stuck status only delays a park, and the agent stays reachable over
 * the transfer path meanwhile; missing a live call costs the call.
 */
export function mayPlaceParkLeg(args: {
  status: string | null | undefined;
  activeCall?: { state?: string | null } | null;
}): boolean {
  if (args.status === 'in-call' || args.status === 'ringing') return false;
  return !isCallStillUp(args.activeCall);
}

/**
 * May a transfer arriving during wrap-up take the browser?
 *
 * Wrap-up is exempt from the busy check above for a good reason: when the
 * previous call has ended and only its disposition is outstanding, the agent
 * IS free, and `activeCallRef` still points at the call that just ended. Left
 * to the busy check, every live-connect during wrap-up would be rejected.
 *
 * The reason was right and the test was wrong. `awaitingDisposition` does not
 * mean "the previous call ended" — it means "a disposition is owed", and one
 * is owed while the agent is on the NEXT call: taking a live-connect during
 * wrap-up queues the old disposition, and clearing that one raises the flag
 * again for the next in the queue. So the flag was routinely true mid-call,
 * the busy check was skipped, and the arriving transfer overwrote the live
 * call — the agent's audio moved to the new merchant and the one they were
 * speaking to vanished. Measured on 2026-09-03/04: 82 calls where an agent
 * was bridged to a second merchant while the first was still connected.
 *
 * So ask the question the exemption always meant to ask — is a call actually
 * up? — instead of asking about the paperwork. Both signals are consulted and
 * either one is enough, because a false "free" costs a live merchant and a
 * false "busy" costs one connect that live-connect retry will re-route.
 */
export function wrapUpMayClaimBrowser(args: {
  awaitingDisposition: boolean;
  activeCall?: { state?: string | null } | null;
  status?: string | null;
}): boolean {
  if (!args.awaitingDisposition) return false;
  if (args.status === 'in-call') return false;
  return !isCallStillUp(args.activeCall);
}

export type WrapUpLead = {
  leadId?: string | null;
  callAttemptId?: string | null;
};

export function wrapUpKey(info: WrapUpLead | null | undefined): string | null {
  if (!info) return null;
  return info.callAttemptId || info.leadId || null;
}

/**
 * Hangup of a call that is no longer activeCallRef used to drop wrap-up.
 * Queue that ended call's lead if we still have it and wrap-up is not open.
 */
export function shouldQueueSupersededWrapUp(args: {
  endedCallId: string;
  activeCallId?: string | null;
  endedLead?: WrapUpLead | null;
  alreadyStartedKeys: Iterable<string>;
}): boolean {
  if (!args.endedCallId) return false;
  if (!args.activeCallId || args.activeCallId === args.endedCallId) return false;
  const key = wrapUpKey(args.endedLead);
  if (!key) return false;
  const started = args.alreadyStartedKeys instanceof Set
    ? args.alreadyStartedKeys
    : new Set(args.alreadyStartedKeys);
  return !started.has(key);
}

/** Park-leg states that mean the leg is established and really holding the browser. */
const PARK_LIVE_STATES = new Set(['active', 'held']);

/**
 * The park leg's id, but only while it is actually up.
 *
 * `occupyingCallIds` above refuses any invite that is not the call already
 * holding the browser, and it runs before every other check — ahead of the
 * wrap-up exemption and ahead of `isCallStillUp`. That is right for a park leg
 * that exists, and catastrophic for one that does not: `ensureParked` claimed
 * the seat the instant `client.newCall()` RETURNED, so the browser began
 * refusing transfers the moment it *asked* to park, before the leg had reached
 * the carrier — and if the leg then failed, every merchant routed inside that
 * window was hung up with SIP 486.
 *
 * Measured 2026-09-08/09, and this is what the guard cost: agents with
 * `park_enabled` refused 11-55% of the transfers sent to them (noor 98/450,
 * suheer 67/307, both 21.8%), while the one agent with park disabled refused
 * 2 of 225 — 0.9%. No park leg has ever reached Telnyx (zero `agentParkLeg`
 * events) and `parkSeat` reads `none` in every routing snapshot, so the park
 * path never carried a single call. Only its guard was live.
 *
 * Note the asymmetry with `isCallStillUp`, which is deliberate and opposite.
 * There an unknown state reads as a live call, because deciding a real
 * conversation is over costs the agent the call they are on. Here an unproven
 * leg reads as no leg, because a park leg that is not up protects nothing, and
 * refusing on it costs a live merchant who is holding on the other end.
 */
export function parkOccupyingCallId(
  parkCall: { id?: string | null; state?: string | null } | null | undefined
): string | null {
  if (!parkCall?.id) return null;
  const state = String(parkCall.state ?? '').trim().toLowerCase();
  return PARK_LIVE_STATES.has(state) ? parkCall.id : null;
}

/**
 * Id the heartbeat must report so the server can recognise this park leg.
 *
 * Redis stores the Telnyx Call Control id from the inbound park webhook.
 * The SDK's `call.id` is a different string, and reporting it made every
 * heartbeat look like a second-tab orphan — which hung up a live Instant
 * Connect call. Prefer the Call Control id; fall back to `call.id` until
 * Telnyx has populated `telnyxIDs`.
 */
export function parkLegReportId(
  parkCall: {
    id?: string | null;
    telnyxIDs?: { telnyxCallControlId?: string | null } | null;
  } | null | undefined
): string | null {
  const ccid = String(parkCall?.telnyxIDs?.telnyxCallControlId || '').trim();
  if (ccid) return ccid;
  const id = String(parkCall?.id || '').trim();
  return id || null;
}
